import { createApi } from '@reduxjs/toolkit/query/react';
import { type PerformanceBanner } from '@strata/react-performance/dist/src/services/interfaces';
import { type PerformanceStatusIndicator } from '@strata/react-status-indicators';

import { PERFORMANCE_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface PerformanceRequirement {
    value_case_id: string;
    value_case_name: string;
    value_case_description: string;
    incentive: number;
    performance_end_date?: string;
    evaluation_end_date?: string;
    status: (typeof PerformanceStatusIndicator.Statuses)[keyof typeof PerformanceStatusIndicator.Statuses];
    banners?: PerformanceBanner[];
}

export const performanceApi = createApi({
    baseQuery: getBaseQuery(PERFORMANCE_API_URL),
    refetchOnMountOrArgChange: true,
    reducerPath: 'performanceApi',
    endpoints: (build) => ({
        getPerformanceEntriesForReferral: build.query<
            PerformanceRequirement[],
            { payerId: string; referralId: string }
        >({
            query: ({ payerId, referralId }) => ({
                url: `payers/referral/${referralId}`,
                headers: { 'x-npi': payerId },
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving performance information.',
            ),
        }),
        getNeedsByDate: build.query<{ needsByDate?: string }, { treatmentId: string }>({
            query: ({ treatmentId }) => ({
                url: `payers/needs_by_date/${treatmentId}`,
            }),
            transformResponse: (response: { needs_by_date?: string }) => ({
                needsByDate: response?.needs_by_date,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving needs by date for the treatment.',
            ),
        }),
    }),
});

export const { useGetPerformanceEntriesForReferralQuery, useGetNeedsByDateQuery } = performanceApi;
